import { RecoilState, Snapshot } from 'recoil';
import { useEffect } from 'react';
import {
  ServiceLevelIndicatorMetadata,
  ServiceLevelIndicatorMetric,
  TaskMetricAttributes,
  TechMetric,
} from 'packages/lib/metrics/types';
import { annotationMetricsTallyAtom } from 'packages/lib/metrics/state';
import { getTokenSilently, userEmail } from 'packages/lib/auth';
import { GIT_SHA_SHORT, ENV, GIT_SHA } from 'packages/env';
import { extractionTokenAtom } from 'packages/at-shell/state/recoil/extraction';
import {
  instructionAtom,
  numFramesAtom,
  numObjectsAtom,
  sampleGroupAtom,
  taskAtom,
  taskGroupAtom,
  toolAliasAtom,
} from 'packages/at-shell/state/recoil/task';
import { post } from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import { debounce } from 'lodash';

import {
  saveMetrics,
  resendSavedMetrics,
} from 'packages/lib/metrics/metric-persistence';

// This is used in index.html to measure api usage
declare global {
  interface Window {
    queueEvent: (event: ServiceLevelIndicatorMetric) => Promise<void>;
  }
}

export const msToS = (ms: number): number => Math.floor(ms / 1000);

export const getMetricAttributes = async (
  snapshot: Snapshot
): Promise<TaskMetricAttributes> => {
  const [
    extraction_token,
    task,
    taskGroup,
    instruction,
    sampleGroup,
    toolAlias,
    numFrames,
    numObjects,
  ] = await Promise.all([
    snapshot.getPromise(extractionTokenAtom),
    snapshot.getPromise(taskAtom),
    snapshot.getPromise(taskGroupAtom),
    snapshot.getPromise(instructionAtom),
    snapshot.getPromise(sampleGroupAtom),
    snapshot.getPromise(toolAliasAtom),
    snapshot.getPromise(numFramesAtom),
    snapshot.getPromise(numObjectsAtom),
  ]);
  return {
    user_email: (await userEmail()) ?? '',
    env: ENV,
    host: window.location.host,
    appv_sha: GIT_SHA_SHORT,
    extraction_token: extraction_token ?? '',
    task_id: task?.token ?? '',
    task_type: task?.type ?? '',
    task_state: task?.annotationState ?? '',
    instruction_name: instruction?.name ?? '',
    sample_group_name: sampleGroup?.name ?? '',
    workflow_name: taskGroup?.pipeline ?? '',
    tool_alias: toolAlias ?? '',
    num_objects: numObjects ?? 0,
    number_frames: numFrames ?? 1,
    request_id: sampleGroup?.requestId ?? '',
  };
};

export const useTrackInputEvents = (onEvent: (event: Event) => void): void => {
  useEffect(() => {
    const regex = /^on(key|pointer)/;
    Object.keys(window).forEach((key) => {
      if (regex.test(key)) {
        window.addEventListener(key.slice(2), onEvent);
      }
    });
    return () => {
      Object.keys(window).forEach((key) => {
        if (regex.test(key)) {
          window.removeEventListener(key.slice(2), onEvent);
        }
      });
    };
  }, [onEvent]);
};

/**
 * helper function to update developer mode / debug UI
 * with running tally of event types we're sending
 */
export const tallyEvents = (
  set: <T>(
    recoilVal: RecoilState<T>,
    valOrUpdater: T | ((currVal: T) => T)
  ) => void,
  eventName: string,
  numberOfEvents: number
): void => {
  set(annotationMetricsTallyAtom, (prev) => ({
    ...prev,
    [eventName]: prev[eventName]
      ? (prev[eventName] as number) + numberOfEvents
      : numberOfEvents,
  }));
};

// This is a tech metrics queue of which inflight events are kept, and removed
// once sent.
let queue: (ServiceLevelIndicatorMetric | TechMetric)[] = [];

const debouncedMetricAPICall = debounce((): void => {
  const metricsToSend = [...queue];
  if (metricsToSend.length === 0) {
    return;
  }
  queue = []; // we've copied the queue, so clear the original

  if (ENV !== 'local') {
    post('techMetrics', '/metrics', metricsToSend, undefined, false)
      .then(() => {
        // If we successfully sent a metric, we can try to flush the saved queue
        void resendSavedMetrics();
      })
      .catch(async () => {
        console.error('Failed to send tech metric, saving to IndexedDB');
        await saveMetrics(metricsToSend, 'tech');
      });
  } else {
    //  console.log('techmetric', metricsToSend);
  }
}, 1000);

export const queueEvent = async (
  event:
    | Omit<ServiceLevelIndicatorMetric, 'metadata'>
    | Omit<TechMetric, 'metadata'>
): Promise<void> => {
  try {
    queue.push({ ...event, metadata: await getMetadata() });
    debouncedMetricAPICall();
  } catch (e) {
    console.error(e);
  }
};
window.queueEvent = queueEvent;

async function getUserGroup(): Promise<string> {
  const token = await getTokenSilently();
  if (token?.issuer === 'contractor') {
    return 'imerit';
  }
  if (token?.issuer === 'primary') {
    return 'motional';
  }
  return 'unknown';
}

async function getMetadata(): Promise<ServiceLevelIndicatorMetadata> {
  try {
    const tags = localStorage.getItem('tech_metrics_tags');
    const token = await getTokenSilently();
    const group = await getUserGroup();
    return {
      env: ENV,
      version: GIT_SHA,
      url: window.location.href,
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      email: token?.user?.email ?? 'null',
      group,
      testKey: !tags ? undefined : JSON.parse(tags).join(','),
      tags: !tags ? [] : JSON.parse(tags),
    };
  } catch (e) {
    throw new Error('could not provide sli metadata for event');
  }
}
